'use client';

import { ChangeEvent, FormEvent, KeyboardEvent, useCallback, useRef, useState } from 'react';
import { useRouter } from '@/i18n/routing';
import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';
import { useClickOutside } from '@/shared/hooks/use-click-outside';
import type { GameInfo } from '@/features/games/shared/types/game-info';
import { Form } from '@/shared/components/form/form';
import { Field } from '@/shared/components/form/field';
import { Input } from '@/shared/components/primitives/input';
import { Loader } from '@/shared/components/primitives/loader';
import { GamesSearchResultsDropdown } from '@/features/games/shared/components/games-searchbar/games-search-results-dropdown';
import { useGamesSearch } from '@/features/games/shared/hooks/use-games-search';
import { scrollElementIntoViewIfNeeded } from '@/features/games/shared/components/games-searchbar/helpers/scroll';

export type GamesSearchInputProps = {
  showPrices?: boolean;
};

const MAX_SEARCH_RESULTS = 10 as const;
const MIN_GAMES_SEARCH_TERM_LENGTH = 3 as const;

export const GamesSearchInput = ({ showPrices = false }: GamesSearchInputProps) => {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const t = useTranslations('Home.header.gameSearch');

  const { displayedResults, hasMoreResults, isLoading, status, error, canSearch } = useGamesSearch({
    searchTerm,
    maxResults: MAX_SEARCH_RESULTS,
    minSearchTermLength: MIN_GAMES_SEARCH_TERM_LENGTH,
  });

  const displayedResultsLength = displayedResults.length;

  useClickOutside(searchContainerRef, () => {
    setShowResults(false);
    setActiveIndex(-1);
  });

  const maintainInputFocus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const scrollActiveResultIntoView = (targetIndex: number) => {
    if (targetIndex < 0) {
      return;
    }

    const resultsList =
      searchContainerRef.current?.querySelector<HTMLElement>('#search-results-list');
    if (!resultsList) {
      return;
    }

    const options = resultsList.querySelectorAll<HTMLElement>('[role="option"]');
    const targetElement = options[targetIndex];

    if (targetElement) {
      scrollElementIntoViewIfNeeded(targetElement, resultsList);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setActiveIndex(-1);
    setShowResults(value.trim().length >= MIN_GAMES_SEARCH_TERM_LENGTH);

    if (value.trim().length < MIN_GAMES_SEARCH_TERM_LENGTH) return;
  };

  const navigateToSearchPage = useCallback(() => {
    const trimmedTerm = searchTerm.trim();

    if (trimmedTerm.length >= MIN_GAMES_SEARCH_TERM_LENGTH) {
      setShowResults(false);
      setActiveIndex(-1);
      setSearchTerm('');
      router.push(`/search?search_query=${encodeURIComponent(trimmedTerm)}`);
    }
  }, [router, searchTerm]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    navigateToSearchPage();
  };

  const handleSelectGame = useCallback(
    (game: GameInfo) => {
      setShowResults(false);
      setActiveIndex(-1);
      setSearchTerm('');

      router.push(game.path);
    },
    [router],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || displayedResultsLength === 0) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = activeIndex < displayedResultsLength - 1 ? activeIndex + 1 : 0;
        setActiveIndex(nextIndex);
        scrollActiveResultIntoView(nextIndex);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const nextIndex = activeIndex > 0 ? activeIndex - 1 : displayedResultsLength - 1;
        setActiveIndex(nextIndex);
        scrollActiveResultIntoView(nextIndex);
        break;
      }
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < displayedResultsLength) {
          event.preventDefault();
          handleSelectGame(displayedResults[activeIndex]);
          maintainInputFocus();
        }
        break;
      case 'Escape':
        setShowResults(false);
        setActiveIndex(-1);
        break;
    }
  };

  const shouldShowResults = showResults && canSearch && status === 'success';

  const noResultsLabel = t('noResultsLabel');

  return (
    <div ref={searchContainerRef} className='relative w-full'>
      <Form onSubmit={handleSubmit}>
        <Field>
          <Input
            ref={inputRef}
            id='games-search-input'
            className={cn('mb-0 w-full', shouldShowResults && 'border-primary')}
            name='search'
            type='text'
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            autoComplete='off'
            role='combobox'
            aria-label={t('searchLabel')}
            aria-expanded={shouldShowResults}
            aria-controls='search-results-list'
            aria-autocomplete='list'
            iconPosition='left'
            icon={isLoading ? <Loader /> : <Search size={22} />}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (canSearch && status === 'success') {
                setShowResults(true);
              }
            }}
          />
        </Field>
      </Form>

      <GamesSearchResultsDropdown
        games={displayedResults}
        isVisible={shouldShowResults}
        activeIndex={activeIndex}
        hasMoreResults={hasMoreResults}
        onSelect={handleSelectGame}
        onShowMore={navigateToSearchPage}
        showMoreLabel={t('showMoreResults')}
        emptyLabel={noResultsLabel}
        showPrices={showPrices}
      />

      {error && <div className='text-error mt-2 text-sm'>{t(error)}</div>}
    </div>
  );
};
